/**
 * AllocationSheet.tsx
 * Bottom sheet for OVER/UNDER selection and impact allocation
 * 
 * BUCKET 4 - NEW FILE
 * 
 * V8 "IMPACT" TERMINOLOGY:
 * - Resource: "TEAM IMPACT" (100 total)
 * - Slider: "PLAYER IMPACT" 
 * - Labels: "Role Player" -> "Playmaker" -> "Game Breaker"
 * - Action: "CONFIRM & NEXT" (not Lock)
 * 
 * BUGFIXES (January 24, 2026):
 * - T2: Slider input constrained to track area (removed z-20, h-full)
 * - T5: Default impact is 25 (Playmaker) not 10 (Role Player)
 */
import React, { useState, useEffect } from 'react';
import { X, Check, ArrowRight } from 'lucide-react';

interface AllocationSheetProps {
  isOpen: boolean;
  onClose: () => void;
  playerName: string;
  statType: string;
  line: number;
  propId: number;
  maxUnits: number;
  onConfirmPlay: (propId: number, side: 'OVER' | 'UNDER', units: number) => void;
}

export const AllocationSheet: React.FC<AllocationSheetProps> = ({
  isOpen,
  onClose,
  playerName,
  statType,
  line,
  propId,
  maxUnits,
  onConfirmPlay,
}) => {
  const [selectedSide, setSelectedSide] = useState<'OVER' | 'UNDER' | null>(null);
  // T5 FIX: Default to 10 (consistent starting point)
  const [impact, setImpact] = useState(10);

  useEffect(() => {
    if (isOpen) {
      setSelectedSide(null);
      // T5 FIX: Reset to 10 (consistent starting point)
      setImpact(10);
    }
  }, [isOpen]);

  const clampedMax = Math.min(50, maxUnits);
  const validImpact = Math.min(Math.max(10, impact), clampedMax);
  
  // Impact label based on value
  const getImpactLabel = () => {
    const range = clampedMax - 10;
    const percentage = range === 0 ? 0 : (validImpact - 10) / range;
    if (percentage <= 0.33) return { label: 'Role Player', color: '#94A3B8' };
    if (percentage <= 0.66) return { label: 'Playmaker', color: '#F59E0B' };
    return { label: 'Game Breaker', color: '#EF4444' };
  };
  
  const strategy = getImpactLabel();

  const handleConfirm = () => {
    if (selectedSide && validImpact >= 10) {
      onConfirmPlay(propId, selectedSide, validImpact);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-50" onClick={onClose} />
      
      <div className="fixed bottom-0 left-0 right-0 bg-slate-900 rounded-t-3xl z-50 border-t border-slate-700 shadow-2xl animate-slide-up">
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-12 h-1.5 bg-slate-700 rounded-full" />
        </div>

        <div className="flex items-center justify-between px-5 pb-4 border-b border-slate-800">
          <div>
            <p className="text-lg font-bold text-white">{playerName}</p>
            <p className="text-xl font-bold text-amber-400">{statType} <span className="text-slate-400 font-normal">• {line}</span></p>
          </div>
          <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-800 hover:bg-slate-700">
            <X size={20} className="text-slate-400" />
          </button>
        </div>

        <div className="p-5">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Select Position</p>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setSelectedSide('OVER')}
              className={`py-4 rounded-xl font-bold text-lg transition-all ${
                selectedSide === 'OVER'
                  ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >ABOVE</button>
            <button
              onClick={() => setSelectedSide('UNDER')}
              className={`py-4 rounded-xl font-bold text-lg transition-all ${
                selectedSide === 'UNDER'
                  ? 'bg-rose-500 text-white shadow-lg shadow-rose-500/30'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >BELOW</button>
          </div>
        </div>

        <div className="px-5 pb-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">Player Impact</p>
            <div className="text-right">
              <p className="text-2xl font-mono font-bold" style={{ color: strategy.color }}>
                {validImpact} <span className="text-slate-500 text-sm">PTS</span>
              </p>
            </div>
          </div>
          
          {/* T2 FIX: Slider with proper input containment */}
          <div className="relative h-4 mb-2">
            {/* Track background */}
            <div className="absolute inset-0 bg-slate-800 rounded-full overflow-hidden">
              <div className="absolute inset-0 opacity-30 bg-gradient-to-r from-slate-400 via-amber-500 to-red-500" />
            </div>
            
            {/* Fill */}
            <div 
              className="absolute left-0 top-0 h-full rounded-full transition-all duration-100"
              style={{ 
                width: `${((validImpact - 10) / (clampedMax - 10)) * 100}%`, 
                backgroundColor: strategy.color 
              }}
            />
            
            {/* Thumb dot */}
            <div 
              className="absolute top-1/2 -translate-y-1/2 w-6 h-6 bg-white rounded-full shadow-lg border-2 transition-all duration-100 pointer-events-none"
              style={{ 
                left: `calc(${((validImpact - 10) / (clampedMax - 10)) * 100}% - 12px)`,
                borderColor: strategy.color
              }}
            />
            
            {/* T2 FIX: Input stays within track - removed z-20 and h-full */}
            <input
              type="range" min={10} max={clampedMax} step={5} value={validImpact}
              onChange={(e) => setImpact(parseInt(e.target.value))}
              className="absolute inset-0 w-full opacity-0 cursor-pointer"
            />
          </div>
          
          {/* Numeric min/max labels */}
          <div className="flex justify-between mt-1 mb-3">
            <span className="text-sm font-mono font-bold text-slate-400">10</span>
            <span className="text-sm font-mono font-bold text-slate-400">{clampedMax}</span>
          </div>
          
          {/* Strategy labels - center is current status, sides are scale endpoints */}
          <div className="flex justify-between items-center text-[10px] text-slate-500 font-bold uppercase">
            <span>Role Player</span>
            <span className="text-sm px-3 py-1 rounded-full bg-slate-800" style={{ color: strategy.color }}>{strategy.label}</span>
            <span>Game Breaker</span>
          </div>
        </div>

        <div className="p-5 pt-0">
          <button
            onClick={handleConfirm}
            disabled={!selectedSide}
            className={`w-full py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2 ${
              selectedSide
                ? 'bg-amber-500 text-slate-900 shadow-lg shadow-amber-500/30 active:scale-[0.98]'
                : 'bg-slate-800 text-slate-600 cursor-not-allowed'
            }`}
          >
            {selectedSide ? (
              <>
                <Check size={20} />
                SET PLAY & NEXT
                <ArrowRight size={20} />
              </>
            ) : (
              'Select Above or Below'
            )}
          </button>
        </div>
        <div className="h-6" />
      </div>

      <style>{`
        @keyframes slide-up { from { transform: translateY(100%); } to { transform: translateY(0); } }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
      `}</style>
    </>
  );
};

export default AllocationSheet;
